import * as React from 'react';
import { getTicketFieldOptions, type TicketFieldRegistryField } from '@/lib/ticket-field-registry-api';
import {
  FIELD_RENDERER_BY_SOURCE_TYPE,
  optionSourceTypeForSourceType,
  type NormalizedTicketFieldOption,
  type TicketFieldRendererType,
  type TicketOptionSourceType,
} from '@/lib/ticket-field-registry';
import type { TicketForm, TicketFormFieldDefinition, TicketFormRule } from '@/lib/ticket-form-api';
import { evaluateTicketFormRules } from '@/lib/ticket-form-rules';
import { Input } from '@/components/ui/input';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';
import { filterGroupsByCountryAndLevel, filterLevelsByCountry } from '@/lib/ticket-group-options';

export type DynamicTicketFieldMode = 'agent' | 'customer' | 'preview';

const EMPTY_OPTIONS: Record<string, NormalizedTicketFieldOption[]> = {};
const LEVEL_CUSTOM_FIELD_KEY = 'cf_level';
const COUNTRY_CUSTOM_FIELD_KEY = 'cf_channelapplication';

function resolveOptionLabels(raw: string[], options: readonly NormalizedTicketFieldOption[]): string[] {
  if (raw.length === 0 || options.length === 0) return raw;
  return raw.map((v) => options.find((opt) => opt.value === v || opt.id === v || opt.label === v)?.label ?? v);
}
const GROUP_SOURCE_TYPES = new Set(['default_group', 'default_internal_group']);

function isGroupField(field: DynamicTicketField): boolean {
  return GROUP_SOURCE_TYPES.has(field.sourceType ?? '');
}

function levelValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (isEmptyValue(value)) return [];
  return [String(value)];
}

function optionMatchesValue(option: NormalizedTicketFieldOption, value: string): boolean {
  const normalizedValue = value.trim().toLowerCase();
  return option.id.trim().toLowerCase() === normalizedValue
    || option.value.trim().toLowerCase() === normalizedValue
    || option.label.trim().toLowerCase() === normalizedValue;
}

export interface DynamicTicketField {
  fieldKey: string;
  label: string;
  description?: string | null;
  labelForCustomers?: string | null;
  sourceType?: string;
  optionSourceType?: TicketOptionSourceType;
  optionSourceId?: string | null;
  rendererType: TicketFieldRendererType | string;
  requiredForAgents?: boolean;
  requiredForCustomers?: boolean;
  requiredForClosure?: boolean;
  displayedToCustomers?: boolean;
  customersCanEdit?: boolean;
  isDefault?: boolean;
  rawChoices?: unknown;
  choices?: unknown;
  dependsOnFieldKey?: string | null;
  enabled: boolean;
  requiredOverride: boolean | null;
  visibleToCustomers: boolean;
}

export interface DynamicTicketFieldRendererProps {
  fields: readonly DynamicTicketField[];
  values: Record<string, unknown>;
  onChange?: (fieldKey: string, value: unknown) => void;
  errors?: Record<string, string>;
  rules?: readonly TicketFormRule[];
  mode?: DynamicTicketFieldMode;
  optionsByField?: Record<string, NormalizedTicketFieldOption[]>;
  requesterOptions?: SearchableSelectOption[];
  requesterLoading?: boolean;
  onRequesterSearch?: (query: string) => void;
  onAttach?: () => void;
  fieldAddon?: (field: DynamicTicketField) => React.ReactNode;
}

function inferredRendererType(sourceType: string): TicketFieldRendererType {
  return FIELD_RENDERER_BY_SOURCE_TYPE[sourceType as keyof typeof FIELD_RENDERER_BY_SOURCE_TYPE] ?? 'single_line_text';
}

function nonEmpty<T>(value: T | null | undefined): T | null {
  if (value == null) return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return value;
}

function formDefinitionFor(
  form: TicketForm,
  fieldKey: string,
): TicketFormFieldDefinition | undefined {
  return form.fieldDefinitions.find((field) => field.fieldKey === fieldKey);
}

export function mergeTicketFormFields(
  form: TicketForm,
  registry: readonly TicketFieldRegistryField[],
): DynamicTicketField[] {
  const registryByKey = new Map(registry.map((field) => [field.fieldKey, field]));

  const merged = [...form.fields]
    .sort((left, right) => left.position - right.position)
    .map((config) => {
      const registryField = registryByKey.get(config.fieldKey);
      const formField = formDefinitionFor(form, config.fieldKey);
      const sourceType = registryField?.sourceType ?? formField?.fieldType ?? 'custom_text';
      const rendererType = registryField?.rendererType
        ?? formField?.rendererType
        ?? inferredRendererType(sourceType);

      return {
        fieldKey: config.fieldKey,
        label: formField?.label ?? registryField?.label ?? config.fieldKey,
        description: formField?.description ?? registryField?.description ?? null,
        labelForCustomers: formField?.labelForCustomers ?? registryField?.labelForCustomers ?? null,
        sourceType,
        optionSourceType: formField?.optionSourceType
          ?? registryField?.optionSourceType
          ?? (registryField?.sourceType ? optionSourceTypeForSourceType(registryField.sourceType) : undefined),
        optionSourceId: formField?.optionSourceId ?? registryField?.optionSourceId ?? null,
        rendererType,
        requiredForAgents: formField?.requiredForAgents ?? registryField?.requiredForAgents ?? false,
        requiredForCustomers: formField?.requiredForCustomers ?? registryField?.requiredForCustomers ?? false,
        requiredForClosure: registryField?.requiredForClosure ?? false,
        displayedToCustomers: formField?.displayedToCustomers ?? registryField?.displayedToCustomers ?? true,
        customersCanEdit: formField?.customersCanEdit ?? registryField?.customersCanEdit ?? true,
        isDefault: formField?.isDefault ?? registryField?.isDefault ?? false,
        rawChoices: nonEmpty(registryField?.rawChoices) ?? nonEmpty(formField?.choices) ?? null,
        choices: nonEmpty(formField?.choices) ?? nonEmpty(registryField?.rawChoices) ?? null,
        dependsOnFieldKey: formField?.dependsOnFieldKey ?? registryField?.dependsOnFieldKey ?? null,
        enabled: config.enabled,
        requiredOverride: config.requiredOverride,
        visibleToCustomers: config.visibleToCustomers,
      };
    });

  // ponytail: reorder dependent fields to appear immediately after their parent
  const ordered: DynamicTicketField[] = [];
  const placed = new Set<string>();
  function place(field: DynamicTicketField) {
    if (placed.has(field.fieldKey)) return;
    placed.add(field.fieldKey);
    ordered.push(field);
    // Place any children that depend on this field immediately after it
    for (const child of merged) {
      if (child.dependsOnFieldKey === field.fieldKey) place(child);
    }
  }
  for (const field of merged) {
    if (!field.dependsOnFieldKey) place(field);
  }
  // Append any remaining fields that weren't placed (orphans)
  for (const field of merged) {
    if (!placed.has(field.fieldKey)) ordered.push(field);
  }

  return ordered;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function normalizeOption(value: unknown, label: unknown, index: number): NormalizedTicketFieldOption | null {
  if (isPrimitive(value)) {
    const normalized = String(value);
    return { id: normalized, value: normalized, label: String(label ?? value), active: true };
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const record = value as Record<string, unknown>;
  const optionValue = record.value ?? record.id ?? record.key ?? index;
  const optionLabel = record.label ?? record.name ?? optionValue;
  return isPrimitive(optionValue)
    ? { id: String(record.id ?? optionValue), value: String(optionValue), label: String(optionLabel), active: record.active !== false }
    : null;
}

function normalizeChoices(value: unknown): NormalizedTicketFieldOption[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) => {
      const option = normalizeOption(entry, entry, index);
      return option ? [option] : [];
    });
  }
  if (!value || typeof value !== 'object') return [];

  return Object.entries(value as Record<string, unknown>).flatMap(([key, entry], index) => {
    if (isPrimitive(entry)) {
      const normalized = String(entry);
      return [{ id: normalized, value: normalized, label: key, active: true }];
    }
    if (Array.isArray(entry) || (entry && typeof entry === 'object')) {
      return [{ id: key, value: key, label: key, active: true }];
    }
    const option = normalizeOption(entry, key, index);
    return option ? [option] : [];
  });
}

function stringValue(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function isEmptyValue(value: unknown): boolean {
  return value === undefined
    || value === null
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);
}

function isVisible(field: DynamicTicketField, mode: DynamicTicketFieldMode): boolean {
  if (!field.enabled) return false;
  if (mode === 'agent' || mode === 'preview') return true;
  return field.visibleToCustomers !== false
    && field.displayedToCustomers !== false
    && field.sourceType !== 'default_internal_group'
    && field.sourceType !== 'default_internal_agent';
}

export function isDynamicFieldRequired(field: DynamicTicketField, mode: DynamicTicketFieldMode = 'agent'): boolean {
  if (field.requiredOverride === true) return true;
  return mode === 'customer' || mode === 'preview'
    ? field.requiredForCustomers === true
    : field.requiredForAgents === true;
}

export function validateDynamicFields(
  fields: readonly DynamicTicketField[],
  values: Record<string, unknown>,
  mode: DynamicTicketFieldMode = 'agent',
  rules: readonly TicketFormRule[] = [],
  requiredMessage: (label: string) => string = (label) => `${label} is required`,
): Record<string, string> {
  const evaluation = evaluateTicketFormRules(fields, rules, values, mode === 'agent' ? 'agent' : 'customer');
  const states = new Map(evaluation.fields.map((field) => [field.fieldKey, field]));
  return Object.fromEntries(
    fields
      .filter((field) => {
        const state = states.get(field.fieldKey);
        return state?.visible && state.required && isEmptyValue(evaluation.values[field.fieldKey]);
      })
      .map((field) => [field.fieldKey, requiredMessage(field.label)]),
  );
}

function optionFields(fields: readonly DynamicTicketField[]) {
  return fields.filter((field) => ['dropdown', 'lookup', 'dependent', 'radio', 'multiselect'].includes(field.rendererType));
}

const SYSTEM_FIELD_LABEL_KEYS = new Set([
  'requester', 'subject', 'description', 'status', 'priority', 'source', 'ticket_type',
  'agent', 'group', 'internal_agent', 'internal_group', 'company',
]);

/** Built-in system fields are translated; account-defined custom field labels stay as authored. */
function localizedBaseLabel(
  field: DynamicTicketField,
  mode: DynamicTicketFieldMode,
  language: string,
  tOr: (key: string, fallback: string) => string,
): string {
  const authored = mode === 'agent' ? field.label : field.labelForCustomers || field.label;
  const systemKey = field.sourceType?.startsWith('default_') ? field.sourceType.slice('default_'.length) : '';
  if (language === 'en' || !SYSTEM_FIELD_LABEL_KEYS.has(systemKey)) return authored;
  return tOr(`fields.label.${systemKey}`, authored);
}

function fieldLabel(baseLabel: string, required: boolean) {
  return `${baseLabel}${required ? ' *' : ''}`;
}

function fieldId(fieldKey: string) {
  return `ticket-field-${fieldKey.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

export function DynamicTicketFieldRenderer({
  fields,
  values,
  onChange,
  errors = {},
  rules = [],
  mode = 'agent',
  optionsByField = EMPTY_OPTIONS,
  requesterOptions = [],
  requesterLoading = false,
  onRequesterSearch,
  onAttach,
  fieldAddon,
}: DynamicTicketFieldRendererProps) {
  const { t, tOr, language } = useI18n();
  const ruleEvaluation = React.useMemo(
    () => evaluateTicketFormRules(fields, rules, values, mode === 'customer' ? 'customer' : 'agent'),
    [fields, rules, values, mode],
  );
  const evaluatedByKey = React.useMemo(
    () => new Map(ruleEvaluation.fields.map((field) => [field.fieldKey, field])),
    [ruleEvaluation.fields],
  );
  const [resolvedOptions, setResolvedOptions] = React.useState<Record<string, NormalizedTicketFieldOption[]>>(optionsByField);
  const [optionErrors, setOptionErrors] = React.useState<Record<string, string>>({});
  const fieldSignature = fields.map((field) => `${field.fieldKey}:${field.rendererType}:${field.dependsOnFieldKey || ''}`).join('|');
  const dependencySignature = optionFields(fields)
    .map((field) => `${field.fieldKey}:${stringValue(field.dependsOnFieldKey ? values[field.dependsOnFieldKey] : '')}`)
    .join('|');
  const hasRequesterSearch = Boolean(onRequesterSearch);
  const levelValue = ruleEvaluation.values[LEVEL_CUSTOM_FIELD_KEY] ?? values[LEVEL_CUSTOM_FIELD_KEY];
  // ponytail: the persisted cf_level value may be a UUID (dropdown option id)
  // rather than the human label like "L1 RO". Resolve it through the Level
  // field's option list so filterGroupsByLevel can pattern-match the label.
  const levelField = fields.find((f) => f.fieldKey === LEVEL_CUSTOM_FIELD_KEY);
  const levelOptions = resolvedOptions[LEVEL_CUSTOM_FIELD_KEY]
    ?? normalizeChoices(levelField?.rawChoices ?? levelField?.choices);
  const selectedLevelValues = React.useMemo(
    () => resolveOptionLabels(levelValues(levelValue), levelOptions),
    [levelValue, levelOptions],
  );
  // Country narrows Group the same way Level does (UUID values resolve to labels).
  const countryValue = ruleEvaluation.values[COUNTRY_CUSTOM_FIELD_KEY] ?? values[COUNTRY_CUSTOM_FIELD_KEY];
  const countryField = fields.find((f) => f.fieldKey === COUNTRY_CUSTOM_FIELD_KEY);
  const countryOptions = resolvedOptions[COUNTRY_CUSTOM_FIELD_KEY]
    ?? normalizeChoices(countryField?.rawChoices ?? countryField?.choices);
  const selectedCountryValues = React.useMemo(
    () => resolveOptionLabels(levelValues(countryValue), countryOptions),
    [countryValue, countryOptions],
  );

  React.useEffect(() => {
    setResolvedOptions(optionsByField);
  }, [optionsByField]);

  React.useEffect(() => {
    let cancelled = false;
    const fieldsToResolve = optionFields(fields).filter((field) => {
      if (field.sourceType === 'default_requester' && hasRequesterSearch) return false;
      return !Object.prototype.hasOwnProperty.call(optionsByField, field.fieldKey) || Boolean(field.dependsOnFieldKey);
    });

    if (fieldsToResolve.length === 0) return undefined;

    async function resolveOptions() {
      const resolved = await Promise.all(fieldsToResolve.map(async (field) => {
        const parentValue = field.dependsOnFieldKey ? values[field.dependsOnFieldKey] : undefined;
        if (field.dependsOnFieldKey && isEmptyValue(parentValue)) {
          return { fieldKey: field.fieldKey, options: [] as NormalizedTicketFieldOption[] };
        }
        try {
          return {
            fieldKey: field.fieldKey,
            options: await getTicketFieldOptions(field.fieldKey, parentValue === undefined ? undefined : String(parentValue)),
          };
        } catch (error) {
          return {
            fieldKey: field.fieldKey,
            error: error instanceof Error ? error.message : t('fields.optionsLoadError', { label: field.label }),
          };
        }
      }));

      if (cancelled) return;
      const nextOptions: Record<string, NormalizedTicketFieldOption[]> = {};
      const nextErrors: Record<string, string> = {};
      for (const result of resolved) {
        if (result.options) nextOptions[result.fieldKey] = result.options;
        else if (result.error) nextErrors[result.fieldKey] = result.error;
      }
      setResolvedOptions((current) => ({ ...current, ...nextOptions }));
      setOptionErrors(nextErrors);
    }

    void resolveOptions();
    return () => {
      cancelled = true;
    };
  }, [dependencySignature, fieldSignature, hasRequesterSearch, mode]);

  function update(field: DynamicTicketField, value: unknown) {
    if (!onChange) return;
    onChange(field.fieldKey, value);
    // Cascade-clear ALL descendants when a parent changes (handles multi-level chains)
    const queue = [field.fieldKey];
    while (queue.length > 0) {
      const parentKey = queue.shift()!;
      for (const child of fields) {
        if (child.dependsOnFieldKey === parentKey) {
          onChange(child.fieldKey, '');
          queue.push(child.fieldKey);
        }
      }
    }
  }

  function optionsFor(field: DynamicTicketField) {
    return resolvedOptions[field.fieldKey] ?? normalizeChoices(field.rawChoices ?? field.choices);
  }

  function filteredOptionsFor(field: DynamicTicketField) {
    const options = optionsFor(field);
    if (isGroupField(field)) return filterGroupsByCountryAndLevel(options, selectedCountryValues, selectedLevelValues);
    if (field.fieldKey === LEVEL_CUSTOM_FIELD_KEY) return filterLevelsByCountry(options, selectedCountryValues);
    return options;
  }

  React.useEffect(() => {
    if (!onChange || (selectedLevelValues.length === 0 && selectedCountryValues.length === 0)) return;

    // A Level from another country (e.g. "L3 RO" after switching to Vietnam)
    // is cleared; the Group check below then re-runs against the new Level.
    if (selectedCountryValues.length > 0 && levelOptions.length > 0 && fields.some((f) => f.fieldKey === LEVEL_CUSTOM_FIELD_KEY)) {
      const currentLevels = levelValues(values[LEVEL_CUSTOM_FIELD_KEY]);
      const allowedLevels = filterLevelsByCountry(levelOptions, selectedCountryValues);
      const isAllowed = (v: string) => allowedLevels.some((option) => option.id === v || optionMatchesValue(option, v));
      if (currentLevels.some((v) => !isAllowed(v))) {
        onChange(
          LEVEL_CUSTOM_FIELD_KEY,
          Array.isArray(values[LEVEL_CUSTOM_FIELD_KEY]) ? currentLevels.filter(isAllowed) : '',
        );
      }
    }

    for (const field of fields) {
      if (!isGroupField(field)) continue;
      // ponytail: only reconcile against server-resolved options. The rawChoices
      // fallback for `group` is legacy Freshdesk dropdown data keyed by numeric
      // external id ("88000151262"), which can never match the group UUID the
      // ticket API returns — reconciling against it cleared a valid group before
      // the real options finished loading.
      const allGroupOptions = resolvedOptions[field.fieldKey];
      if (!allGroupOptions || allGroupOptions.length === 0) continue;

      const matchingGroups = filterGroupsByCountryAndLevel(allGroupOptions, selectedCountryValues, selectedLevelValues);
      if (matchingGroups.length === allGroupOptions.length) continue;

      const currentValue = stringValue(values[field.fieldKey]);
      if (currentValue && !matchingGroups.some((option) => optionMatchesValue(option, currentValue))) {
        onChange(field.fieldKey, '');
      }
    }
  }, [fields, levelOptions, onChange, resolvedOptions, selectedCountryValues, selectedLevelValues, values]);

  function searchableOptions(field: DynamicTicketField) {
    if (field.sourceType === 'default_requester' && requesterOptions.length > 0) return requesterOptions;
    return filteredOptionsFor(field).filter((option) => option.active !== false).map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }

  // ponytail: check full ancestor chain — don't list nested children until every ancestor has a value
  function isAncestorChainSatisfied(field: DynamicTicketField): boolean {
    if (!field.dependsOnFieldKey) return true;
    let ancestorKey: string | null | undefined = field.dependsOnFieldKey;
    while (ancestorKey) {
      if (isEmptyValue(values[ancestorKey])) return false;
      const ancestor = fields.find((f) => f.fieldKey === ancestorKey);
      ancestorKey = ancestor?.dependsOnFieldKey;
    }
    return true;
  }

  function renderField(field: DynamicTicketField) {
    const evaluated = evaluatedByKey.get(field.fieldKey);
    if (!evaluated?.visible) return null;

    const required = evaluated.required;
    const baseLabel = localizedBaseLabel(field, mode, language, tOr);
    const label = fieldLabel(baseLabel, required);
    const id = fieldId(field.fieldKey);
    const error = errors[field.fieldKey] || optionErrors[field.fieldKey];
    const editable = Boolean(onChange) && (mode === 'preview' || (!evaluated.readonly && (mode === 'agent' || field.customersCanEdit !== false)));
    const disabled = !editable;
    const value = ruleEvaluation.values[field.fieldKey];
    const rendererType = field.rendererType;
    const options = filteredOptionsFor(field).filter((option) => option.active !== false);
    const rawValue = stringValue(value);
    const selectedOption = options.find((option) => option.value === rawValue)
      ?? options.find((option) => option.id === rawValue)
      ?? options.find((option) => option.label === rawValue)
      ?? options.find((option) => option.label.trim().toLowerCase() === rawValue.trim().toLowerCase());
    const selectValue = selectedOption?.value ?? rawValue;
    const selectOptions = searchableOptions(field);
    const control = (() => {
      if (rendererType === 'lookup' || rendererType === 'dropdown' || rendererType === 'dependent') {
        return (
          <SearchableSelect
            label={label}
            options={selectOptions}
            value={selectValue}
            onChange={(nextValue) => update(field, nextValue)}
            onSearch={field.sourceType === 'default_requester' ? onRequesterSearch : undefined}
            loading={field.sourceType === 'default_requester' ? requesterLoading : undefined}
            placeholder={field.dependsOnFieldKey && options.length === 0 ? t('fields.selectParentFirst') : t('fields.selectPlaceholder', { label: baseLabel })}
            error={error}
            disabled={disabled}
          />
        );
      }

      if (rendererType === 'checkbox') {
        return (
          <div>
            <label htmlFor={id} className="flex items-center gap-2 text-sm font-medium text-ink-muted">
              <input
                id={id}
                type="checkbox"
                checked={Boolean(value)}
                onChange={(event) => update(field, event.target.checked)}
                disabled={disabled}
                className="h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-500"
              />
              {label}
            </label>
            {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
          </div>
        );
      }

      if (rendererType === 'radio') {
        return (
          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-ink-muted">{label}</legend>
            <div className="space-y-2">
              {options.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-ink-muted">
                  <input
                    type="radio"
                    name={id}
                    value={option.value}
                    checked={stringValue(value) === option.value}
                    onChange={() => update(field, option.value)}
                    disabled={disabled}
                    className="h-4 w-4 border-line-strong text-brand-700 focus:ring-brand-500"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
          </fieldset>
        );
      }

      if (rendererType === 'multiselect') {
        const selectedValues = Array.isArray(value) ? value.map(String) : [];
        return (
          <fieldset>
            <legend className="mb-1 block text-sm font-medium text-ink-muted">{label}</legend>
            <div className="space-y-2">
              {options.map((option) => (
                <label key={option.value} className="flex items-center gap-2 text-sm text-ink-muted">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={(event) => update(field, event.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((selected) => selected !== option.value))}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-line-strong text-brand-700 focus:ring-brand-500"
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
          </fieldset>
        );
      }

      if (field.sourceType === 'default_description' || rendererType === 'multi_line_text') {
        if (field.sourceType === 'default_description') {
          return (
            <RichTextEditor
              label={label}
              value={stringValue(value)}
              onChange={(nextValue) => update(field, nextValue)}
              placeholder={t('fields.enterPlaceholder', { label: baseLabel })}
              onAttach={onAttach}
              error={error}
              disabled={disabled}
            />
          );
        }
        return (
          <div>
            <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink-muted">{label}</label>
            <textarea
              id={id}
              value={stringValue(value)}
              onChange={(event) => update(field, event.target.value)}
              rows={4}
              disabled={disabled}
              aria-invalid={error ? 'true' : undefined}
              className={cn(
                'w-full rounded-md border border-line-strong px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50',
                error && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500',
              )}
              placeholder={t('fields.enterPlaceholder', { label: baseLabel })}
            />
            {error && <p className="mt-1 text-xs text-danger-600" role="alert">{error}</p>}
          </div>
        );
      }

      if (rendererType === 'date' || rendererType === 'number' || rendererType === 'decimal') {
        return (
          <Input
            id={id}
            type={rendererType === 'date' ? 'date' : 'number'}
            step={rendererType === 'decimal' ? 'any' : undefined}
            label={label}
            value={stringValue(value)}
            onChange={(event) => update(field, event.target.value)}
            error={error}
            disabled={disabled}
          />
        );
      }

      return (
        <Input
          id={id}
          label={label}
          value={stringValue(value)}
          onChange={(event) => update(field, event.target.value)}
          error={error}
          disabled={disabled}
          placeholder={t('fields.enterPlaceholder', { label: baseLabel })}
        />
      );
    })();

    return (
      <div key={field.fieldKey} className="space-y-1">
        {control}
        {field.description && <p className="text-xs text-ink-subtle">{field.description}</p>}
        {fieldAddon?.(field)}
      </div>
    );
  }

  return <div className="space-y-4">{fields
    .filter((field) => isVisible(field, mode) && evaluatedByKey.get(field.fieldKey)?.visible !== false && isAncestorChainSatisfied(field))
    .map(renderField)}
  </div>;
}
